import { Link } from 'react-router-dom';
import { Pencil, Trash2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useState } from 'react';

interface Customer {
  id: string;
  name: string;
  tax_id: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  address: string | null;
}

interface ClienteRowIncompletoProps {
  customer: Customer;
  onEdit: () => void;
  onDeleted: () => void;
}

// Format CPF/CNPJ
function maskTaxId(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

function StatusBadge({ value }: { value: string | null }) {
  if (value) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[hsl(142,70%,35%)]">
        <Check className="h-3 w-3" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-[hsl(45,90%,85%)] text-[hsl(45,90%,25%)]">
      PENDENTE
    </span>
  );
}

export function ClienteRowIncompleto({ customer, onEdit, onDeleted }: ClienteRowIncompletoProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customer.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setConfirmOpen(false);
      onDeleted();
    }
  });

  return (
    <>
      <Link 
        to={`/clientes/${customer.id}`}
        className="grid grid-cols-[2fr_1fr_6rem_6rem_6rem_8rem] gap-4 p-4 items-center hover:bg-[hsl(var(--basic-50))] transition-colors"
      >
        <span className="font-bold text-[hsl(var(--basic-900))] truncate">
          {customer.name}
        </span>
        <span className="text-muted-foreground font-mono text-sm">
          {maskTaxId(customer.tax_id)}
        </span>
        <span className="flex justify-center">
          <StatusBadge value={customer.email} />
        </span>
        <span className="flex justify-center">
          <StatusBadge value={customer.whatsapp || customer.phone} />
        </span>
        <span className="flex justify-center">
          <StatusBadge value={customer.address} />
        </span>
        <div className="flex justify-end gap-2" onClick={(e) => e.preventDefault()}>
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8"
            onClick={(e) => {
              e.preventDefault();
              onEdit();
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button 
            variant="outline" 
            size="icon" 
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={(e) => {
              e.preventDefault();
              setConfirmOpen(true);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </Link>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Excluir Cliente"
        description="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita."
        onConfirm={() => deleteMutation.mutate()}
        confirmText="Excluir"
        variant="destructive"
      />
    </>
  );
}
